// hooks/useScrollAutoHide.ts
// ─────────────────────────────────────────────────────────────────────────
// Hook générique pour cacher/montrer automatiquement un header local ET
// la tab bar globale (via useTabBarVisibility) en fonction de la direction
// du scroll — synchronisés sur le même geste. Fournit aussi un fallback
// manuel via double-tap, réutilisable identiquement sur n'importe quel
// écran (pas seulement actu.tsx).
//
// Utilisation dans un écran (ex: actu.tsx, avec header local) :
//
//   const { handleScroll, handleDoubleTap } = useScrollAutoHide({
//     onShow: showHeader,   // fonction locale qui anime le header vers 0
//     onHide: hideHeader,   // fonction locale qui anime le header vers -H
//   });
//
//   <ScrollView onScroll={handleScroll} scrollEventThrottle={16}
//     onTouchEnd={handleDoubleTap} ... />
//
// Utilisation sur un écran SANS header local (juste la tab bar) :
//
//   const { handleScroll, handleDoubleTap } = useScrollAutoHide();
//   <ScrollView onScroll={handleScroll} scrollEventThrottle={16}
//     onTouchEnd={handleDoubleTap} ... />
//
// Si l'écran n'a pas de header local à gérer (juste la tab bar), on peut
// omettre onShow/onHide — seule la tab bar sera pilotée par les deux
// mécanismes (scroll auto + double-tap manuel).
// ─────────────────────────────────────────────────────────────────────────

import { useRef, useCallback, useEffect } from 'react';
// ✅ Correct (chemin actuel réel)
import { useTabBarVisibility } from '@/components/CustomTabBar';

const SCROLL_DELTA_THRESHOLD = 5;    // ignorer les micro-mouvements
const SCROLL_END_DELAY_MS    = 800;  // délai avant réapparition auto
const DOUBLE_TAP_DELAY_MS    = 300;  // fenêtre de détection du double-tap

type UseScrollAutoHideOptions = {
  /** Appelé pour réafficher le header local de l'écran (optionnel) */
  onShow?: () => void;
  /** Appelé pour cacher le header local de l'écran (optionnel) */
  onHide?: () => void;
  /** Callback additionnel, ex. pour le "load more" ou le tracking de vues */
  onScrollExtra?: (e: any) => void;
};

export function useScrollAutoHide({ onShow, onHide, onScrollExtra }: UseScrollAutoHideOptions = {}) {
  const { setTabBarHidden } = useTabBarVisibility();

  const lastScrollY            = useRef(0);
  const scrollEndTimer         = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHiddenByScroll       = useRef(false);
  // État CIBLE courant (là où on a demandé à la tab bar d'aller), utilisé
  // à la fois par le double-tap (pour savoir dans quel sens basculer) et
  // par showAll/hideAll eux-mêmes comme garde anti-doublon.
  //
  // IMPORTANT : contrairement à un verrou temporel (setTimeout), cette
  // garde ne bloque JAMAIS un appel légitime — elle ignore seulement les
  // appels REDONDANTS (demander de cacher alors que c'est déjà la cible
  // actuelle). Un verrou temporel pouvait avaler un showAll() arrivé
  // pendant sa fenêtre de blocage (ex: scrollEndTimer qui se déclenche
  // pile pendant qu'un hideAll() venait de verrouiller la transition),
  // laissant la tab bar bloquée à mi-animation sans qu'aucun appel
  // ultérieur ne la débloque. Ici, tant que la cible demandée diffère de
  // la cible actuelle, l'appel passe toujours.
  const currentTarget           = useRef<'shown' | 'hidden'>('shown');
  // Anti-triple-tap / debounce simple pour la détection du double-tap
  const lastTapAt               = useRef<number | null>(null);

  const showAll = useCallback(() => {
    if (currentTarget.current === 'shown') return; // déjà la cible → no-op
    currentTarget.current = 'shown';
    onShow?.();
    setTabBarHidden(false);
    isHiddenByScroll.current = false;
  }, [onShow, setTabBarHidden]);

  const hideAll = useCallback(() => {
    if (currentTarget.current === 'hidden') return; // déjà la cible → no-op
    currentTarget.current = 'hidden';
    onHide?.();
    setTabBarHidden(true);
    isHiddenByScroll.current = true;
  }, [onHide, setTabBarHidden]);

  const handleScroll = useCallback((e: any) => {
    const { contentOffset } = e.nativeEvent;
    const currentY = contentOffset.y;
    const delta    = currentY - lastScrollY.current;

    if (currentY <= 0) {
      // Tout en haut du feed (ou bounce iOS) → toujours visible
      showAll();
    } else if (Math.abs(delta) > SCROLL_DELTA_THRESHOLD) {
      if (delta > 0) hideAll();  // scroll vers le bas → cacher
      else           showAll();  // scroll vers le haut → montrer
    }
    lastScrollY.current = currentY;

    // Réinitialiser le timer de fin de scroll à chaque event
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(() => {
      if (isHiddenByScroll.current) showAll();
    }, SCROLL_END_DELAY_MS);

    // Callback additionnel de l'écran (load more, tracking vues, etc.)
    onScrollExtra?.(e);
  }, [showAll, hideAll, onScrollExtra]);

  // ── Double-tap manuel (fallback identique sur tous les écrans) ────────
  // Bascule l'état visible/caché indépendamment du scroll. Utilisable via
  // onTouchEnd sur le conteneur principal ou le ScrollView de l'écran.
  const toggleVisibility = useCallback(() => {
    if (currentTarget.current === 'hidden') showAll();
    else hideAll();
  }, [showAll, hideAll]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTapAt.current && now - lastTapAt.current < DOUBLE_TAP_DELAY_MS) {
      toggleVisibility();
      lastTapAt.current = null; // évite qu'un triple-tap ne re-déclenche
    } else {
      lastTapAt.current = now;
    }
  }, [toggleVisibility]);

  // Nettoyage du timer au démontage
  useEffect(() => {
    return () => {
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
      // Sécurité : ne pas laisser la tab bar cachée si l'écran est
      // démonté pendant qu'elle était masquée par le scroll.
      setTabBarHidden(false);
    };
  }, [setTabBarHidden]);

  return { handleScroll, handleDoubleTap, showAll, hideAll };
}
